import { AlertTriangle, Eye } from "lucide-react";
import { useMemo, useState } from "react";

import { Label } from "components/ui/label";

import { WARNING_BOX } from "./WarningNote";

import { LinkifiedText } from "../../notifications/LinkifiedText";
import {
  isPersonalised,
  renderText,
  templateProblem,
} from "../announcement-placeholders";
import {
  defaultPreviewRecipient,
  missingValueWarnings,
  truncatedCount,
  useAnnouncementRecipients,
  type AnnouncementRecipient,
  type MissingValueWarning,
  type RecipientsState,
} from "../use-announcement-recipients";

export interface Personalisation {
  personalised: boolean;
  /** Why the message cannot be sent as typed, or null. Blocks sending. */
  problem: string | null;
  recipients: RecipientsState;
  warnings: MissingValueWarning[];
  truncated: number;
}

/**
 * Everything the composer needs to know about a message's placeholders
 * (TNG-95): whether it has any, whether one is a typo, and, once it has some,
 * who it reaches and which of them lack a value.
 */
export function useAnnouncementPersonalisation(
  eventId: string,
  title: string,
  body: string,
  maxTitle: number,
  maxBody: number,
  /** The seating {dinner_table} reads from (TNG-94), if chosen. */
  seatingId?: string | null,
): Personalisation {
  const personalised = isPersonalised(title) || isPersonalised(body);
  const recipients = useAnnouncementRecipients(
    eventId,
    personalised,
    seatingId,
  );
  const problem = useMemo(() => {
    const inTitle = templateProblem(title);
    if (inTitle) return `In the title: ${inTitle}`;
    const inBody = templateProblem(body);
    return inBody ? `In the message: ${inBody}` : null;
  }, [title, body]);
  const { warnings, truncated } = useMemo(() => {
    if (!personalised || recipients.status !== "success")
      return { warnings: [], truncated: 0 };
    return {
      warnings: missingValueWarnings(title, body, recipients.recipients),
      truncated: truncatedCount(
        title,
        body,
        recipients.recipients,
        maxTitle,
        maxBody,
      ),
    };
  }, [personalised, recipients, title, body, maxTitle, maxBody]);
  return { personalised, problem, recipients, warnings, truncated };
}

/**
 * The non-blocking notes about a personalised message: who lacks a value and
 * sees a fallback, and how many copies will be cut to fit. Shown under the
 * composer and again in the confirm dialog.
 */
export function PersonalisationWarnings({
  personalisation,
  testId,
}: {
  personalisation: Personalisation;
  testId: string;
}) {
  const { warnings, truncated } = personalisation;
  if (warnings.length === 0 && truncated === 0) return null;
  return (
    <div
      role="status"
      className={`space-y-1 ${WARNING_BOX}`}
      data-testid={testId}
    >
      {warnings.map((warning) => (
        <p key={warning.key} className="flex gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{warning.text}</span>
        </p>
      ))}
      {truncated > 0 && (
        <p className="flex gap-2" data-testid={`${testId}-truncated`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {truncated} {truncated === 1 ? "message" : "messages"} will be
            shortened to fit once the details are filled in, and end in
            &ldquo;&hellip;&rdquo;.
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * "As seen by <recipient>": the message filled in for one person, chosen
 * from everyone it reaches. Starts on somebody missing a value, since that
 * copy is the one most worth checking.
 */
export function AnnouncementPreview({
  title,
  body,
  maxTitle,
  maxBody,
  personalisation,
}: {
  title: string;
  body: string;
  maxTitle: number;
  maxBody: number;
  personalisation: Personalisation;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const { recipients } = personalisation;
  if (!personalisation.personalised || personalisation.problem) return null;

  if (recipients.status === "loading" || recipients.status === "idle")
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="announcement-preview-loading"
      >
        Loading who this reaches&hellip;
      </p>
    );
  if (recipients.status === "error")
    return (
      <p
        className="text-sm text-destructive-text"
        data-testid="announcement-preview-error"
      >
        The preview could not load the recipients: {recipients.error}
      </p>
    );

  const list = recipients.recipients;
  if (list.length === 0)
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="announcement-preview-empty"
      >
        Nobody is registered for this event yet, so there is no one to preview.
      </p>
    );
  const fallback = defaultPreviewRecipient(title, body, list);
  const shown: AnnouncementRecipient | undefined =
    list.find((recipient) => recipient.uid === chosen) ?? fallback;
  if (!shown) return null;

  return (
    <section
      className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
      data-testid="announcement-preview"
    >
      <div className="space-y-1.5">
        <Label
          htmlFor="announcement-preview-recipient"
          className="flex items-center gap-2"
        >
          <Eye className="h-4 w-4 shrink-0" aria-hidden />
          As seen by
        </Label>
        <select
          id="announcement-preview-recipient"
          data-testid="announcement-preview-recipient"
          value={shown.uid}
          onChange={(event) => setChosen(event.target.value)}
          className="h-11 w-full rounded-md border border-input bg-background px-3 text-base text-foreground sm:text-sm"
        >
          {list.map((recipient) => (
            <option key={recipient.uid} value={recipient.uid}>
              {recipient.label}
            </option>
          ))}
        </select>
      </div>
      {/* The same type as the participant's notification: a semibold title in
          the foreground colour and the body in the muted one, on a card. */}
      <div className="rounded-lg border border-border bg-card px-3 py-2 text-card-foreground">
        <p
          className="text-sm font-semibold text-foreground wrap-anywhere"
          data-testid="announcement-preview-title"
        >
          {renderText(title.trim(), shown.values, maxTitle)}
        </p>
        <p
          className="whitespace-pre-line text-sm text-muted-foreground wrap-anywhere"
          data-testid="announcement-preview-body"
        >
          <LinkifiedText
            text={renderText(body.trim(), shown.values, maxBody)}
          />
        </p>
      </div>
      {!shown.hasRosterRow && (
        <p className="text-xs text-muted-foreground">
          The attendee list gives no name for this person, so their name comes
          from their profile when the message goes out; the preview cannot show
          it.
        </p>
      )}
    </section>
  );
}
